import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderOpen, Plus } from 'lucide-react';
import Skeleton from 'components/ui/skeleton';
import EmptyState from 'components/ui/EmptyState';
import * as CollectionsModule from 'modules/collections';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { CollectionCard } from 'pages/Library/CollectionCard';
import { CreateTile } from 'pages/Library/CreateTile';
import { AssignCollectionDialog } from './AssignCollectionDialog';

export interface GroupMaterialsTabProps {
  teamId: string;
}

const GRID = 'grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-4';

/** Only shared collections appear — documents-shared-with-a-group isn't a backend concept yet. */
export const GroupMaterialsTab: React.FC<GroupMaterialsTabProps> = ({ teamId }) => {
  const { t } = useTranslation();
  const [shareOpen, setShareOpen] = useState(false);
  const canShare = usePermission(PERMISSIONS.TEAMS_EDIT);

  const { collections, isLoading, refetch } = CollectionsModule.Hooks.useByTeam({ teamId, enabled: Boolean(teamId) });

  if (isLoading) {
    return (
      <div className={GRID} aria-hidden>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-[148px] rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <>
      {collections.length === 0 ? (
        <EmptyState
          icon={<FolderOpen size={28} />}
          title={t('group_materials_empty_title')}
          description={t('group_materials_empty_desc')}
          action={
            canShare
              ? { label: t('group_share_material'), icon: <Plus className="h-4 w-4" />, onClick: () => setShareOpen(true) }
              : undefined
          }
        />
      ) : (
        <div className={GRID}>
          {collections.map((c) => (
            <CollectionCard
              key={c.id}
              name={c.name}
              itemCount={c.itemCount}
              updatedAt={c.updatedAt}
              shareState="team"
              to={`/app/collections/${c.id}`}
            />
          ))}
          {canShare && <CreateTile label={t('group_share_material')} onClick={() => setShareOpen(true)} />}
        </div>
      )}

      <AssignCollectionDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        teamId={teamId}
        assignedCollectionIds={collections.map((c) => c.id)}
        onAssigned={() => refetch()}
      />
    </>
  );
};

export default GroupMaterialsTab;
